import type {
  IDocumentReaderUploadBatchCreated,
  IDocumentReaderUploadFile,
  IDocumentReaderUploadFileInput,
  IDocumentReaderUploadLimits,
  IDocumentReaderUploadReference,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import { S3FileNameHelper } from '@commenda/utils/files/s3-file-name-helper';

import {
  DataRoomDropKind,
  DataRoomFileState,
  type IDataRoomDropCheck,
  type IDataRoomDropState,
  type IDataRoomFileProgress,
  type IDataRoomLocalFile,
  type IDataRoomPartRange,
  type IDataRoomResumeMatch,
  type IDataRoomSetContents,
  type IDataRoomSetSize,
  type IDataRoomUploadProgress,
} from './types';

export class DataRoomUploadHelper {
  static readonly PARTS_IN_FLIGHT_PER_FILE = 4;

  static readonly PARTS_IN_FLIGHT = 6;

  static readonly RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000];

  static readonly MAX_VISIBLE_FILE_ROWS = 50;

  static readonly MAX_LISTED_NAMES = 5;

  private static readonly BYTES_PER_MB = 1_000_000;

  private static readonly BYTES_PER_GB = 1_000_000_000;

  private static readonly MAX_FILE_NAME_LENGTH = 255;

  private static readonly MAX_RELATIVE_PATH_LENGTH = 1_024;

  /** Byte range of one part: every part is `partSizeBytes` long except the last. */
  static partRange(
    partNumber: number,
    sizeBytes: number,
    partSizeBytes: number
  ): IDataRoomPartRange {
    const start = (partNumber - 1) * partSizeBytes;
    return {
      partNumber,
      start,
      end: Math.min(start + partSizeBytes, sizeBytes),
    };
  }

  static missingPartNumbers(file: Readonly<IDataRoomLocalFile>): number[] {
    return Array.from(
      { length: file.partCount },
      (_, index) => index + 1
    ).filter((partNumber) => !file.storedEntityTags.has(partNumber));
  }

  static missingPartCount(files: readonly IDataRoomLocalFile[]): number {
    return files.reduce(
      (sum, file) => sum + DataRoomUploadHelper.missingPartNumbers(file).length,
      0
    );
  }

  static storedBytes(file: Readonly<IDataRoomLocalFile>): number {
    return [...file.storedEntityTags.keys()].reduce((sum, partNumber) => {
      const range = DataRoomUploadHelper.partRange(
        partNumber,
        file.selectedFile.file.size,
        file.partSizeBytes
      );
      return sum + (range.end - range.start);
    }, 0);
  }

  /** "4.8 GB" from 1 GB up, "40.0 MB" below, one decimal either way. */
  static formatBytes(bytes: number): string {
    return bytes >= DataRoomUploadHelper.BYTES_PER_GB
      ? `${(bytes / DataRoomUploadHelper.BYTES_PER_GB).toFixed(1)} GB`
      : `${(bytes / DataRoomUploadHelper.BYTES_PER_MB).toFixed(1)} MB`;
  }

  static percent(sentBytes: number, totalBytes: number): number {
    if (totalBytes <= 0) return 100;
    return Math.min(100, Math.floor((sentBytes / totalBytes) * 100));
  }

  static totalBytes(uploads: readonly CommendaAiUploadFile[]): number {
    return uploads.reduce((sum, { file }) => sum + file.size, 0);
  }

  static splitEmpty(uploads: readonly CommendaAiUploadFile[]): {
    uploads: CommendaAiUploadFile[];
    emptyNames: string[];
  } {
    return {
      uploads: uploads.filter(({ file }) => file.size > 0),
      emptyNames: uploads
        .filter(({ file }) => file.size === 0)
        .map(({ path }) => path),
    };
  }

  /** The refusal to show before any request, or null when the room fits the limits. `current` is the
   * set the drop joins; the server checks the limits again on every add. */
  static getLimitError(
    uploads: readonly CommendaAiUploadFile[],
    limits: Readonly<IDocumentReaderUploadLimits>,
    current: Readonly<IDataRoomSetSize> = { fileCount: 0, totalBytes: 0 }
  ): string | null {
    if (uploads.length === 0)
      return 'Every file in this selection is empty, so there is nothing to upload.';
    const isFirstDrop = current.fileCount === 0;
    const totalBytes =
      current.totalBytes + DataRoomUploadHelper.totalBytes(uploads);
    const fileCount = current.fileCount + uploads.length;
    const limitBytes = DataRoomUploadHelper.formatBytes(limits.maxRoomBytes);
    if (totalBytes > limits.maxRoomBytes)
      return isFirstDrop
        ? `These documents come to ${DataRoomUploadHelper.formatBytes(totalBytes)}; the limit is ${limitBytes}.`
        : `With these documents the upload would come to ${DataRoomUploadHelper.formatBytes(totalBytes)}; the limit is ${limitBytes}.`;
    if (fileCount > limits.maxFileCount)
      return isFirstDrop
        ? `This upload has ${fileCount} files; the limit is ${limits.maxFileCount}.`
        : `With these documents the upload would have ${fileCount} files; the limit is ${limits.maxFileCount}.`;
    return null;
  }

  /** Sets aside empty files and, when the drop joins a set, files already in it; then checks the
   * limits against the whole set. A drop of duplicates only is no error. */
  static checkDrop(
    selected: readonly CommendaAiUploadFile[],
    limits: Readonly<IDocumentReaderUploadLimits>,
    set: Readonly<IDataRoomSetContents> | null
  ): IDataRoomDropCheck {
    const { uploads: nonEmpty, emptyNames } =
      DataRoomUploadHelper.splitEmpty(selected);
    const { uploads, duplicateNames } = set
      ? DataRoomUploadHelper.splitDuplicates(
          nonEmpty,
          DataRoomUploadHelper.getDuplicateKeys(set)
        )
      : { uploads: nonEmpty, duplicateNames: [] };
    const limitError =
      uploads.length === 0 && duplicateNames.length > 0
        ? null
        : DataRoomUploadHelper.getLimitError(
            uploads,
            limits,
            set ? DataRoomUploadHelper.getSetSize(set) : undefined
          );
    return { uploads, emptyNames, duplicateNames, limitError };
  }

  /** The set a drop joins: the rows shown plus the files still waiting to be added. */
  static getSetSize({
    rows,
    waitingUploads,
  }: Readonly<IDataRoomSetContents>): IDataRoomSetSize {
    return {
      fileCount: rows.length + waitingUploads.length,
      totalBytes:
        rows.reduce((sum, row) => sum + row.sizeBytes, 0) +
        DataRoomUploadHelper.totalBytes(waitingUploads),
    };
  }

  /** The same file chosen again has the same relative path, size and last-modified time. */
  static duplicateKey({ file, path }: Readonly<CommendaAiUploadFile>): string {
    return `${path}|${file.size}|${file.lastModified}`;
  }

  /** The keys of every file already in the set, shown or waiting to be added. */
  static getDuplicateKeys({
    rows,
    waitingUploads,
  }: Readonly<IDataRoomSetContents>): Set<string> {
    return new Set([
      ...rows.flatMap((row) => (row.duplicateKey ? [row.duplicateKey] : [])),
      ...waitingUploads.map((upload) =>
        DataRoomUploadHelper.duplicateKey(upload)
      ),
    ]);
  }

  /** Skips files already in the set and repeats inside the same drop. */
  static splitDuplicates(
    uploads: readonly CommendaAiUploadFile[],
    existingKeys: ReadonlySet<string>
  ): { uploads: CommendaAiUploadFile[]; duplicateNames: string[] } {
    const seenKeys = new Set(existingKeys);
    return uploads.reduce<{
      uploads: CommendaAiUploadFile[];
      duplicateNames: string[];
    }>(
      (split, upload) => {
        const key = DataRoomUploadHelper.duplicateKey(upload);
        if (seenKeys.has(key)) split.duplicateNames.push(upload.path);
        else split.uploads.push(upload);
        seenKeys.add(key);
        return split;
      },
      { uploads: [], duplicateNames: [] }
    );
  }

  static getDropKind(state: Readonly<IDataRoomDropState>): DataRoomDropKind {
    if (!state.hasSession && state.isCreating) return DataRoomDropKind.QUEUE;
    if (!state.hasSession && state.hasResumeRecord)
      return DataRoomDropKind.RESUME;
    if (state.hasSession && state.isMissingFiles && !state.isUploading)
      return DataRoomDropKind.RESUME;
    return state.hasSession ? DataRoomDropKind.APPEND : DataRoomDropKind.CREATE;
  }

  /** True when removing one more file would leave the set empty, so the whole upload is cancelled. */
  static isLastRemainingFile(
    fileCount: number,
    pendingFileCount: number
  ): boolean {
    return fileCount <= 1 && pendingFileCount === 0;
  }

  static formatNameList(names: readonly string[]): string {
    const shown = names
      .slice(0, DataRoomUploadHelper.MAX_LISTED_NAMES)
      .join(', ');
    const hiddenCount = names.length - DataRoomUploadHelper.MAX_LISTED_NAMES;
    return hiddenCount > 0 ? `${shown} and ${hiddenCount} more` : shown;
  }

  /** One message naming the files a resume could not pair and those it can no longer finish. */
  static getResumeError(
    unmatchedNames: readonly string[],
    abortedNames: readonly string[]
  ): string | null {
    const messages = [
      unmatchedNames.length > 0
        ? `These files were not in your selection, so choose them too: ${DataRoomUploadHelper.formatNameList(unmatchedNames)}.`
        : null,
      abortedNames.length > 0
        ? `These files can no longer be finished, so cancel and upload the documents again: ${DataRoomUploadHelper.formatNameList(abortedNames)}.`
        : null,
    ].filter((message): message is string => message !== null);
    return messages.length > 0 ? messages.join(' ') : null;
  }

  /** True when a dropped selection was read from inside a folder. */
  static hasFolderFiles(uploads: readonly CommendaAiUploadFile[]): boolean {
    return uploads.some(({ path }) => path.includes('/'));
  }

  static toFileInput({
    file,
    path,
  }: Readonly<CommendaAiUploadFile>): IDocumentReaderUploadFileInput {
    const fileName = S3FileNameHelper.sanitize(file.name).slice(
      0,
      DataRoomUploadHelper.MAX_FILE_NAME_LENGTH
    );
    const relativePath =
      path && path !== file.name
        ? path.slice(-DataRoomUploadHelper.MAX_RELATIVE_PATH_LENGTH)
        : undefined;
    return {
      fileName,
      sizeBytes: file.size,
      ...(relativePath ? { relativePath } : {}),
      ...(file.type ? { contentType: file.type } : {}),
    };
  }

  static splitBatches<T>(items: readonly T[], batchSize: number): T[][] {
    return Array.from(
      { length: Math.ceil(items.length / batchSize) },
      (_, index) => items.slice(index * batchSize, (index + 1) * batchSize)
    );
  }

  /** Pairs re-selected files with one batch's server files by name, size and last-modified time
   * (when the record kept it). Stored parts and their ETags seed each pair, so only missing parts go. */
  static matchResumeFiles(
    uploads: readonly CommendaAiUploadFile[],
    serverFiles: readonly IDocumentReaderUploadFile[],
    batchIndex: number,
    lastModifiedInBatch: readonly number[] | undefined
  ): IDataRoomResumeMatch {
    const localByKey = new Map<string, CommendaAiUploadFile[]>();
    uploads.forEach((upload) => {
      const input = DataRoomUploadHelper.toFileInput(upload);
      const key = DataRoomUploadHelper.matchKey(
        input.relativePath ?? input.fileName,
        upload.file.size,
        lastModifiedInBatch ? upload.file.lastModified : null
      );
      localByKey.set(key, [...(localByKey.get(key) ?? []), upload]);
    });

    const files: IDataRoomLocalFile[] = [];
    const unmatchedNames: string[] = [];
    serverFiles.forEach((serverFile) => {
      const position = DataRoomUploadHelper.filePosition(serverFile.id);
      const lastModified = lastModifiedInBatch?.[position];
      const key = DataRoomUploadHelper.matchKey(
        serverFile.relativePath ?? serverFile.fileName,
        serverFile.sizeBytes,
        lastModifiedInBatch ? (lastModified ?? -1) : null
      );
      const selectedFile = localByKey.get(key)?.shift();
      if (!selectedFile) {
        unmatchedNames.push(serverFile.relativePath ?? serverFile.fileName);
        return;
      }
      files.push({
        selectedFile,
        fileId: serverFile.id,
        batchIndex,
        partCount: serverFile.partCount,
        partSizeBytes: serverFile.partSizeBytes,
        storedEntityTags: new Map(
          serverFile.uploadedParts.map((part) => [
            part.partNumber,
            part.entityTag,
          ])
        ),
      });
    });

    return { files, unmatchedNames };
  }

  /** Pairs one created batch with the files sent in it. Empty files are never sent, so the backend's
   * list is the request in order; any other length means the pairing cannot be trusted. */
  static pairCreatedFiles(
    uploads: readonly CommendaAiUploadFile[],
    created: Readonly<IDocumentReaderUploadBatchCreated>
  ): IDataRoomLocalFile[] | null {
    if (created.files.length !== uploads.length) return null;
    return created.files.map((createdFile, index) => ({
      selectedFile: uploads[index],
      fileId: createdFile.id,
      batchIndex: created.batchIndex,
      partCount: createdFile.partCount,
      partSizeBytes: createdFile.partSizeBytes,
      storedEntityTags: new Map<number, string>(),
    }));
  }

  static toFileProgress(
    file: Readonly<IDataRoomLocalFile>
  ): IDataRoomFileProgress {
    return {
      fileId: file.fileId,
      name: file.selectedFile.path,
      sizeBytes: file.selectedFile.file.size,
      sentBytes: DataRoomUploadHelper.storedBytes(file),
      state: DataRoomFileState.UPLOADING,
      error: null,
      duplicateKey: DataRoomUploadHelper.duplicateKey(file.selectedFile),
    };
  }

  /** A file the server already holds in full needs no local copy to show as stored. */
  static toStoredRow(
    serverFile: Readonly<IDocumentReaderUploadFile>
  ): IDataRoomFileProgress {
    return {
      fileId: serverFile.id,
      name: serverFile.relativePath ?? serverFile.fileName,
      sizeBytes: serverFile.sizeBytes,
      sentBytes: serverFile.sizeBytes,
      state: DataRoomFileState.STORED,
      error: null,
      duplicateKey: null,
    };
  }

  /** The room a message carries once every row is stored. */
  static toUploadReference(
    uploadId: string,
    rows: readonly IDataRoomFileProgress[]
  ): IDocumentReaderUploadReference {
    return {
      uploadId,
      fileCount: rows.length,
      totalSizeBytes: rows.reduce((sum, row) => sum + row.sizeBytes, 0),
    };
  }

  static summarise(
    files: readonly IDataRoomFileProgress[]
  ): IDataRoomUploadProgress {
    return {
      files: [...files],
      sentBytes: files.reduce((sum, file) => sum + file.sentBytes, 0),
      totalBytes: files.reduce((sum, file) => sum + file.sizeBytes, 0),
      isStored:
        files.length > 0 &&
        files.every((file) => file.state === DataRoomFileState.STORED),
    };
  }

  /** Failed files first, then those still moving, so the rows worth reading are never cut off. */
  static visibleFileRows(
    files: readonly IDataRoomFileProgress[]
  ): IDataRoomFileProgress[] {
    const order = [
      DataRoomFileState.FAILED,
      DataRoomFileState.UPLOADING,
      DataRoomFileState.STORED,
    ];
    return [...files]
      .sort(
        (first, second) =>
          order.indexOf(first.state) - order.indexOf(second.state)
      )
      .slice(0, DataRoomUploadHelper.MAX_VISIBLE_FILE_ROWS);
  }

  private static matchKey(
    name: string,
    sizeBytes: number,
    lastModified: number | null
  ): string {
    return lastModified === null
      ? `${name}|${sizeBytes}`
      : `${name}|${sizeBytes}|${lastModified}`;
  }

  /** A file id is `<batchId>-<position>`, its place in the batch request. */
  private static filePosition(fileId: string): number {
    return Number(fileId.slice(fileId.lastIndexOf('-') + 1));
  }
}
